import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Loader2, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface DestructiveConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  items: string[];
  onConfirm: () => Promise<void>;
  isLoading?: boolean;
}

export function DestructiveConfirmModal({
  open,
  onOpenChange,
  title,
  description,
  items,
  onConfirm,
  isLoading = false,
}: DestructiveConfirmModalProps) {
  const [step, setStep] = useState<'warning' | 'verify'>('warning');
  const [verificationCode, setVerificationCode] = useState('');
  const [enteredCode, setEnteredCode] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [codeError, setCodeError] = useState('');

  const resetState = () => {
    setStep('warning');
    setVerificationCode('');
    setEnteredCode('');
    setIsSending(false);
    setCodeError('');
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetState();
    }
    onOpenChange(open);
  };

  const handleContinue = async () => {
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setVerificationCode(code);
    setIsSending(true);
    try {
      const { error } = await supabase.functions.invoke('send-verification-code', {
        body: { code, operation: title },
      });
      if (error) {
        toast({ title: 'Erro ao enviar email de verificacao. Tente novamente.', variant: "destructive" });
        return;
      }
      setStep('verify');
    } finally {
      setIsSending(false);
    }
  };

  const handleVerify = async () => {
    if (enteredCode !== verificationCode) {
      setCodeError('Codigo incorreto. Verifique seu email e tente novamente.');
      return;
    }
    setCodeError('');
    await onConfirm();
    handleOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        {step === 'warning' ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                {title}
              </DialogTitle>
              <DialogDescription asChild>
                <div className="space-y-4 pt-2">
                  <p>
                    Esta acao e <strong>IRREVERSIVEL</strong>. Os seguintes dados serao permanentemente deletados:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    {items.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSending}>
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleContinue}
                disabled={isSending}
              >
                {isSending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Continuar
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Verifique seu email</DialogTitle>
              <DialogDescription asChild>
                <div className="space-y-2 pt-2">
                  <div className="flex items-start gap-2">
                    <Mail className="h-4 w-4 mt-0.5 shrink-0" />
                    <p>
                      Enviamos um codigo de 6 digitos para seu email. Digite abaixo para confirmar a operacao.
                    </p>
                  </div>
                </div>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-2">
              <Label htmlFor="verification-code">Codigo de verificacao</Label>
              <Input
                id="verification-code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={enteredCode}
                onChange={(e) => setEnteredCode(e.target.value)}
                placeholder="000000"
                className="font-mono text-center text-lg tracking-widest"
              />
              {codeError && (
                <p className="text-sm text-destructive">{codeError}</p>
              )}
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isLoading}>
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleVerify}
                disabled={enteredCode.length < 6 || isLoading}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Confirmar operacao
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
